import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { fetchLayout, resolveConfiguration } from './configuration-api';
import type { ConfigurationApiClient, ConfigurationScope } from './types';

/**
 * What the Angular `ConfigurationState` did by hand (a Map keyed `feature:orgNodeId:viewMode`,
 * a 5-minute TTL, an in-flight guard, "last loaded" bookkeeping, a stale-user check on every
 * read and write, clearing on logout) is a query key here:
 *
 *   ['configuration', 'feature', userId, feature, orgNodeId, viewMode]
 *   ['configuration', 'layout',  appId, userId, orgNodeId, viewMode]
 *
 * - The user id is in the key, so a response that lands after a logout or user switch is filed
 *   under the old user and can never be read as the new one's.
 * - The key holds exactly what the request sends. The Angular cache keyed tyre-rankings by a
 *   walked-up org node while still requesting the selected one; here the two cannot disagree.
 * - A view-mode toggle is a different key, hence a fresh fetch — no invalidation needed.
 * - No feature-level reuse across org nodes: Angular's opt-in list for that was empty, because
 *   every feature's texts, capabilities and availability can differ per node.
 */

/** Angular's `CONFIGURATION_CACHE_TTL_MS`: configuration rarely changes within a session. */
export const CONFIGURATION_STALE_TIME_MS = 5 * 60_000;

export const configurationKeys = {
  all: ['configuration'] as const,
  feature: (scope: ConfigurationScope, feature: string) =>
    ['configuration', 'feature', scope.userId, feature, scope.orgNodeId, scope.viewMode] as const,
  layout: (appId: string, scope: ConfigurationScope) =>
    ['configuration', 'layout', appId, scope.userId, scope.orgNodeId, scope.viewMode] as const,
};

/**
 * One feature's resolved configuration. Disabled until an org node is selected — the Angular
 * state skipped the load in the same case, and the backend would otherwise resolve against the
 * user's home node and cache that under a key that says "no node".
 */
export const featureConfigurationQueryOptions = (
  client: ConfigurationApiClient,
  feature: string,
  scope: ConfigurationScope,
) =>
  // `client` is the transport (the app's one session client), not an input to the answer.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: configurationKeys.feature(scope, feature),
    queryFn: ({ signal }) => resolveConfiguration(client, feature, scope, signal),
    enabled: scope.orgNodeId !== null,
    // The scope is in the key: a switch is a different query, not one to reset.
    meta: { scope: 'keyed' },
    staleTime: CONFIGURATION_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

/** The app's navigation. Resolves without a selected node too (against the user's home node). */
export const layoutQueryOptions = (
  client: ConfigurationApiClient,
  appId: string,
  scope: ConfigurationScope,
) =>
  // `client` is the transport (the app's one session client), not an input to the answer.
  // eslint-disable-next-line @tanstack/query/exhaustive-deps
  queryOptions({
    queryKey: configurationKeys.layout(appId, scope),
    queryFn: ({ signal }) => fetchLayout(client, appId, scope, signal),
    meta: { scope: 'keyed' },
    staleTime: CONFIGURATION_STALE_TIME_MS,
    refetchOnWindowFocus: false,
    retry: false,
  });

/**
 * Drop a feature's configuration at one org node, in every view mode — what a write that feeds
 * resolution (user preferences) must be followed by. Same reach as Angular's
 * `invalidateCacheForFeature`.
 */
export function invalidateFeatureConfiguration(
  queryClient: QueryClient,
  scope: Pick<ConfigurationScope, 'userId' | 'orgNodeId'>,
  feature: string,
): Promise<void> {
  return queryClient.invalidateQueries({
    queryKey: ['configuration', 'feature', scope.userId, feature, scope.orgNodeId],
  });
}

/**
 * Forget every configuration answer. Call on logout: the user id in the key already keeps the
 * next user from reading them, but a logged-out tab should not keep another user's texts and
 * permissions in memory either.
 */
export function removeAllConfiguration(queryClient: QueryClient): void {
  queryClient.removeQueries({ queryKey: configurationKeys.all });
}
